import { useComparisonMatchingSSE } from '@/hooks/useSSEProgress';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertCircle, CheckCircle2, ChevronDown, ChevronUp, Loader2, PlayCircle, FileSearch, Layers, Home, AlignJustify, Activity } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Comparison, ComparisonStatus } from '@/api/generated/client/models';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useComparisonLogs } from '@/hooks/use-comparisons';
import { getCategorizedLogsArray, type LogCategory, type ReasoningLogEntry } from '@/lib/reasoning-log-utils';

interface ComparisonProgressCardProps {
    comparisonId: number;
    initialData?: Comparison;
    className?: string;
}

const CATEGORY_CONFIG: Record<LogCategory, { label: string, icon: any, color: string }> = {
    extraction: { label: 'Extraction Analysis', icon: FileSearch, color: 'text-blue-500' },
    level: { label: 'Level Matching', icon: Layers, color: 'text-indigo-500' },
    room: { label: 'Room Matching', icon: Home, color: 'text-purple-500' },
    line_item: { label: 'Line Item Matching', icon: AlignJustify, color: 'text-emerald-500' },
    general: { label: 'General Processing', icon: Activity, color: 'text-slate-500' }
};

export function ComparisonProgressCard({ comparisonId, initialData, className }: ComparisonProgressCardProps) {
    const [isExpanded, setIsExpanded] = useState(false);

    // Determine initial status
    const isInitiallyComplete = initialData?.status === ComparisonStatus.Completed || initialData?.status === 'completed' as any;
    const isInitiallyFailed = initialData?.status === ComparisonStatus.Failed || initialData?.status === 'failed' as any;
    const isInitiallyActive = !isInitiallyComplete && !isInitiallyFailed;

    const shouldConnect = isInitiallyActive || isExpanded;

    const { progress, isComplete: sseComplete, isFailed: sseFailed, error: sseError, reasoningLog: sseLogs } = useComparisonMatchingSSE(comparisonId, shouldConnect);

    const isComplete = isInitiallyComplete || sseComplete;
    const isFailed = isInitiallyFailed || sseFailed;

    // Derive display error from SSE or initial data
    let displayError = initialData?.errorMessage || (isInitiallyFailed ? initialData?.statusMessage : undefined) || sseError;

    // Ensure failed state always shows an error message
    if (isFailed && !displayError) {
        displayError = "Comparison failed. No detailed error message available.";
    }

    // Lazy load logs when expanded and completed (not receiving SSE anymore)
    const { data: fetchedLogs, isFetching: isFetchingLogs } = useComparisonLogs(comparisonId, {
        enabled: isExpanded && (isInitiallyComplete || sseComplete)
    });

    // Use SSE logs if available, otherwise use fetched logs
    // Both are already normalized by their respective hooks
    const logs = useMemo((): ReasoningLogEntry[] => {
        if (sseLogs && sseLogs.length > 0) {
            return sseLogs;
        }
        return fetchedLogs || [];
    }, [sseLogs, fetchedLogs]);

    // Categorize logs using helper function
    const categorizedLogs = useMemo(() => {
        return getCategorizedLogsArray(logs);
    }, [logs]);

    // ...

    // Track progress locally to handle -1 (ignore) and preserve last known good value
    const [percentage, setPercentage] = useState(isInitiallyComplete ? 100 : 0);

    useEffect(() => {
        if (isComplete) {
            setPercentage(100);
        } else if (progress?.progress !== undefined && progress.progress >= 0) {
            setPercentage(progress.progress);
        }
    }, [progress?.progress, isComplete]);
    const stage = progress?.message || (progress?.stage ? progress.stage.replace(/_/g, ' ') : (isComplete ? 'Completed' : 'Processing...'));
    const formattedStage = progress?.message ? stage : stage.replace(/\b\w/g, c => c.toUpperCase());

    if (!progress && !isComplete && !isFailed && !initialData) {
        return (
            <Card className={cn("mb-6 border-blue-200 bg-blue-50/50", className)}>
                <CardContent className="pt-6 flex items-center gap-4">
                    <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                    <p className="text-sm text-blue-700">Connecting to comparison workflow...</p>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className={cn("mb-6 border-blue-200 shadow-md transition-all duration-300", className)}>
            <CardHeader className="pb-3 bg-blue-50/30 rounded-t-lg border-b border-blue-100">
                <div className="flex justify-between items-start">
                    <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                            {isComplete ? (
                                <CheckCircle2 className="h-6 w-6 text-green-600" />
                            ) : isFailed ? (
                                <AlertCircle className="h-6 w-6 text-red-600" />
                            ) : (
                                <Activity className="h-5 w-5 animate-pulse" />
                            )}
                        </div>
                        <div>
                            <CardTitle className="text-base font-semibold text-blue-950">
                                {isComplete ? `${initialData?.name || `Comparison #${comparisonId}`} Complete` : isFailed ? `${initialData?.name || `Comparison #${comparisonId}`} Failed` : `Running ${initialData?.name || `Comparison #${comparisonId}`}`}
                            </CardTitle>
                            <CardDescription className="text-blue-800/80 text-xs mt-1">
                                {isFailed ? 'Error occurred' : formattedStage}
                            </CardDescription>
                        </div>
                    </div>
                    {isFailed && (
                        <Badge variant="destructive" className="ml-auto">Failed</Badge>
                    )}
                    {!isFailed && !isComplete && (
                        <Badge variant="outline" className="ml-auto bg-blue-100/50 text-blue-700 border-blue-200">
                            {Math.round(percentage)}%
                        </Badge>
                    )}
                </div>
            </CardHeader>
            <CardContent className="pt-4">
                {displayError ? (
                    <div className="p-3 bg-red-50 text-red-800 text-sm rounded-md border border-red-200 mb-4">
                        <p className="font-medium">Error details:</p>
                        <p>{displayError}</p>
                    </div>
                ) : (
                    <>
                        <div className="space-y-2 mb-4">
                            {!isComplete && (
                                <>
                                    <div className="flex justify-between text-xs text-muted-foreground mb-1">
                                        <span>Progress</span>
                                        <span>{Math.round(percentage)}%</span>
                                    </div>
                                    <Progress value={percentage} className="h-2 bg-blue-100" indicatorClassName={isComplete ? "bg-green-500" : "bg-blue-500"} />
                                </>
                            )}
                        </div>

                        {/* Reasoning Logs Section */}
                        <div className="mt-4 border rounded-md overflow-hidden bg-white/50">
                            <div
                                className="px-3 py-2 bg-slate-50 border-b flex justify-between items-center cursor-pointer hover:bg-slate-100 transition-colors"
                                onClick={() => setIsExpanded(!isExpanded)}
                            >
                                <div className="flex items-center gap-2 text-xs font-medium text-slate-700">
                                    <PlayCircle className="h-3.5 w-3.5 text-blue-500" />
                                    <span>Analysis Logs</span>
                                    {isFetchingLogs && <Loader2 className="h-3 w-3 animate-spin text-blue-500 ml-1" />}
                                    {!isFetchingLogs && logs.length > 0 && <Badge variant="secondary" className="h-5 px-1.5 text-[10px] bg-slate-200 text-slate-700">{logs.length}</Badge>}
                                </div>
                                <Button variant="ghost" size="icon" className="h-6 w-6">
                                    {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                                </Button>
                            </div>

                            {isExpanded && (
                                <div className="bg-slate-50/30 p-2">
                                    {categorizedLogs.length === 0 ? (
                                        <p className="text-xs text-muted-foreground text-center py-4">No reasoning logs available yet.</p>
                                    ) : (
                                        <Accordion type="multiple" defaultValue={['general', 'extractions', 'level', 'room', 'line_item']} className="w-full space-y-2">
                                            {categorizedLogs.map(([category, logs]) => {
                                                const config = CATEGORY_CONFIG[category];
                                                const Icon = config.icon;
                                                return (
                                                    <AccordionItem key={category} value={category} className="border rounded-md bg-white border-slate-200 px-0">
                                                        <AccordionTrigger className="px-3 py-2 text-xs hover:no-underline hover:bg-slate-50 data-[state=open]:rounded-t-md data-[state=closed]:rounded-md">
                                                            <div className="flex items-center gap-2">
                                                                <Icon className={cn("h-3.5 w-3.5", config.color)} />
                                                                <span>{config.label}</span>
                                                                <span className="text-muted-foreground ml-1 text-[10px]">({logs.length})</span>
                                                            </div>
                                                        </AccordionTrigger>
                                                        <AccordionContent className="px-0 pb-0">
                                                            <LogList logs={logs} />
                                                        </AccordionContent>
                                                    </AccordionItem>
                                                );
                                            })}
                                        </Accordion>
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
}

function LogList({ logs }: { logs: ReasoningLogEntry[] }) {
    return (
        <ScrollArea className="h-[120px] w-full border-t bg-slate-50/50">
            <div className="p-3 space-y-2">
                {logs.map((log, i) => (
                    <div key={log.id || i} className="flex gap-2 text-xs animate-in fade-in duration-300">
                        <span className="text-slate-400 font-mono text-[10px] w-4 mt-0.5">{i + 1}.</span>
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                                {log.createdAt && (
                                    <span className="text-slate-400 text-[9px]">
                                        {new Date(log.createdAt).toLocaleTimeString()}
                                    </span>
                                )}
                            </div>
                            <span className="text-slate-600 leading-snug block">{log.reasoningContent}</span>
                            {log.tokenCount && log.tokenCount > 0 && (
                                <span className="text-slate-400 text-[9px] mt-1 block">
                                    {log.tokenCount.toLocaleString()} tokens
                                </span>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </ScrollArea>
    );
}
